import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Col, Empty, message, Result, Row, Spin, Tabs, Tooltip } from 'antd';
import axios from 'axios';
import { isEmpty, isNil } from 'ramda';
import { SideWithAsset } from 'shared-types';

import AssetsSidebar from 'components/AssetsSidebar/AssetsSidebar';
import PageCardEditor from 'components/Book/PageCardEditor';
import ProductSidesPreviewsList from 'components/Book/PagesList';
import ResultOutput from 'components/Book/ResultOutput';
import Box from 'components/Box/Box';
import DragDropOverlay from 'components/DragDropOverlay/DragDropOverlay';

import useLayoutStore from 'hooks/stores/useLayoutStore';
import useProductsStore from 'hooks/stores/useProductsStore';
import useSessionStore from 'hooks/stores/useSessionStore';
import useQueryParams from 'hooks/useQueryParams';
import useRealTimeUpdateSidePreview from 'hooks/useRealTimeUpdateSidePreview';

import API from 'services/API';

import { sanitizeFileName } from 'utils/misc';

import { SContainer, SLoaderContainer } from './Uploader.styles';

const Book = () => {
  const { t } = useTranslation();
  const { sessionId } = useQueryParams();
  const queryClient = useQueryClient();

  const {
    products,
    selectedSideId,
    selectedProductId,
    setProducts,
    setSelectedSideId,
    setSelectedProductId,
    getSelectedProduct,
    getSelectedProductSides,
  } = useProductsStore((state) => {
    return {
      products: state.products,
      selectedSideId: state.selectedSideId,
      selectedProductId: state.selectedProductId,
      setProducts: state.setProducts,
      setSelectedSideId: state.setSelectedSideId,
      setSelectedProductId: state.setSelectedProductId,
      getSelectedProduct: state.getSelectedProduct,
      getSelectedProductSides: state.getSelectedProductSides,
    };
  });

  const setVisible = useLayoutStore((state) => state.setShouldDisplayAssetsProcessing);

  // State voor global drag & drop
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const dragTimeoutRef = useRef<number | null>(null);
  const dragCheckIntervalRef = useRef<number | null>(null);

  // Scroll-container van de Sideslist (AssetsSidebar's <Row>), gedeeld met de virtualizer in
  // PagesList.tsx zodat die weet welk DOM-element daadwerkelijk scrolt.
  const sideslistScrollRef = useRef<HTMLDivElement>(null);

  // State for pending product selection
  const [pendingProductId, setPendingProductId] = useState<string | null>(null);

  // State for inline editing
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [editingCount, setEditingCount] = useState<number>(0);

  // State for bulk quantity management panel
  const [showQuantityPanel, setShowQuantityPanel] = useState<boolean>(false);

  // Nieuwe state voor PDF previews
  // We gebruiken een Map met side IDs als keys voor wanneer er meerdere pagina's zijn
  const [pdfPreviews, setPdfPreviews] = useState<Map<string, string>>(new Map());

  // Handler functie voor het opslaan van nieuwe previews
  const handlePdfPreviewGenerated = (sideId: string, previewUrl: string) => {
    setPdfPreviews((prevPreviews) => {
      const newPreviews = new Map(prevPreviews);
      newPreviews.set(sideId, previewUrl);
      return newPreviews;
    });
  };

  const { setSessionId, setSession, session } = useSessionStore((state) => {
    return {
      setSessionId: state.setSessionId,
      setSession: state.setSession,
      session: state.session,
    };
  });

  const { isLoading: isLoadingProducts, isError: isProductsError } = useQuery(
    'products',
    () => API.getProducts(sessionId),
    {
      enabled: !!sessionId,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      onSuccess: (data) => {
        setProducts(data);
      },
    },
  );

  const { isLoading, isError: isSessionError } = useQuery(
    'session',
    () => API.getSession(sessionId),
    {
      enabled: !!sessionId,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setSessionId(sessionId);
        setSession(data);
      },
    },
  );

  const { isVisible } = useRealTimeUpdateSidePreview();

  // Determine variant configuration
  const rawVariantsConfig = (session?.options as any)?.variantsConfig;
  const variantsConfig = rawVariantsConfig?.enabled === false ? undefined : rawVariantsConfig;

  // Only show variant functionality if variantsConfig is explicitly defined and not disabled
  const variantFeaturesEnabled = !!variantsConfig;

  // With fixedQuantities the distribution is set at session creation and cannot be changed
  const hasFixedQuantities =
    variantFeaturesEnabled &&
    Array.isArray(variantsConfig.fixedQuantities) &&
    variantsConfig.fixedQuantities.length > 0;

  // With allowChangeFixedQuantities the fixedQuantities are only prefilled: the customer may
  // redistribute the (fixed) total over the variants, but not change the number of variants
  const quantityLocked = hasFixedQuantities && variantsConfig.allowChangeFixedQuantities !== true;

  // Check if rightBar is enabled (default = true for backward compatibility)
  const isRightBarEnabled = session?.options?.ui?.rightBar?.enabled !== false;
  const maxVariants = variantFeaturesEnabled ? variantsConfig.maxCount : 1; // Default to single variant when no config
  const needsExactCount = variantFeaturesEnabled
    ? (variantsConfig.needsExactCount ?? false)
    : false;

  // Track both total quantity (sum of all product counts) and variant count (number of different variants)
  const currentTotalQuantity = (products as any[]).reduce(
    (total: number, product: any) => total + (product.count || 1),
    0,
  );
  const currentVariantCount = products.length;

  // Auto-select pending product when it becomes available
  useEffect(() => {
    if (pendingProductId && products.length > 0) {
      const productExists = (products as any[]).some((p) => p.id === pendingProductId);
      if (productExists) {
        setSelectedProductId(pendingProductId);
        setPendingProductId(null); // Clear pending state
      }
    }
  }, [products, pendingProductId, setSelectedProductId]);

  // Auto-create products for exact count mode (only when variant features are enabled)
  useEffect(() => {
    if (
      session &&
      products.length > 0 &&
      variantFeaturesEnabled &&
      !hasFixedQuantities &&
      needsExactCount &&
      maxVariants > 1
    ) {
      if (currentVariantCount < maxVariants) {
        const missingVariants = maxVariants - currentVariantCount;
        console.log(
          `Exact count mode: need ${maxVariants} variants, have ${currentVariantCount}, creating ${missingVariants} more`,
        );

        // Create missing variants automatically
        for (let i = 0; i < missingVariants; i++) {
          handleAddProduct();
        }
      }
    }
  }, [
    session,
    products.length,
    variantFeaturesEnabled,
    needsExactCount,
    maxVariants,
    currentVariantCount,
  ]);

  // Get the currently selected product and sides using computed selectors
  const selectedProduct = getSelectedProduct() || products[0];
  const sides = getSelectedProductSides() as SideWithAsset[];
  const shouldShowBook = !isEmpty(sides) && sides.some((side) => !isNil(side?.assetId));
  const isSidesFull = sides.length >= (session?.sidesCount ?? 0);
  const isUploadEnabled = session?.options?.ui?.enableUpload !== false && !isSidesFull;
  const firstPageIndex = 0; // default to first page
  const foundSideIndex = sides.findIndex((side) => side.id === selectedSideId);
  const selectedSideIndex = foundSideIndex > -1 ? foundSideIndex : firstPageIndex;

  // Functie om isDragging te updaten met debounce om knipperen te voorkomen
  const updateDraggingState = useCallback(
    (newState: boolean) => {
      const now = Date.now();
      if (newState) {
        // Als we isDragging aanzetten, doe dat onmiddellijk
        if (!isDragging) {
          lastDragStateChangeRef.current = now;
          setIsDragging(true);
        }
      } else {
        // Als we isDragging uitzetten, minimale wachttijd
        if (isDragging && now - lastDragStateChangeRef.current > 300) {
          lastDragStateChangeRef.current = now;
          setIsDragging(false);
        }
      }
    },
    [isDragging],
  );

  // Drag & drop handlers
  const handleDragEnter = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (!isUploadEnabled) return;

      // Controleren of er bestanden worden gesleept
      if (e.dataTransfer?.types.includes('Files')) {
        // Clear any existing timeout to ensure we don't hide the overlay prematurely
        if (dragTimeoutRef.current !== null) {
          window.clearTimeout(dragTimeoutRef.current);
          dragTimeoutRef.current = null;
        }
        updateDraggingState(true);
      }
    },
    [updateDraggingState, isUploadEnabled],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();

      // Check if the event is on the document element (html) or body
      // This indicates the cursor has left the browser window
      const target = e.target as HTMLElement;

      // We only want to hide the overlay when the cursor truly leaves the window
      if (target === document.documentElement || target === document.body) {
        // Clear any existing timeout first
        if (dragTimeoutRef.current !== null) {
          window.clearTimeout(dragTimeoutRef.current);
        }

        // Set a short timeout to hide the overlay
        dragTimeoutRef.current = window.setTimeout(() => {
          updateDraggingState(false);
          dragTimeoutRef.current = null;
        }, 150); // Slightly longer timeout to avoid flickering
      }
    },
    [updateDraggingState],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (!isUploadEnabled) return;

      // We don't set isDragging on every dragOver to avoid constant re-renders
      if (e.dataTransfer?.types.includes('Files')) {
        e.dataTransfer.dropEffect = 'copy';
        // Don't call updateDraggingState on dragOver as it causes flickering
      }
    },
    [isUploadEnabled],
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();

      // Clear any timeout when a drop occurs
      if (dragTimeoutRef.current !== null) {
        window.clearTimeout(dragTimeoutRef.current);
        dragTimeoutRef.current = null;
      }

      // Explicitly set isDragging to false on drop
      setIsDragging(false);
      lastDragStateChangeRef.current = Date.now();

      if (!isUploadEnabled) {
        if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
          message.error(t('BookPage.ErrorMessages.MaxPagesReached'));
        }
        return;
      }

      // Controleer of er bestanden zijn gedropt
      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files);

        // Verwerk de bestanden
        try {
          // Controleer bestanden
          const validFiles = files.filter((file) => {
            const type = file.type.toLowerCase();
            return type.includes('pdf') || type.includes('image');
          });

          if (validFiles.length === 0) {
            message.error(t('BookPage.ErrorMessages.OnlyPdfOrImages'));
            return;
          }

          // Upload elk bestand
          for (const file of validFiles) {
            setVisible(true);
            try {
              const timestampedFileName = sanitizeFileName(file.name);
              const signedUrl = await API.getSignedUrl(sessionId, file, timestampedFileName);
              if (!signedUrl) throw new Error('Failed to get signed url');

              const response = await axios.put(signedUrl, file, {
                headers: {
                  'Content-Type': file.type,
                },
              });

              if (response.status !== 200) throw new Error('Upload failed');

              // Bericht naar worker om bestanden te verwerken
              await API.postNewUploadMessage(
                sessionId,
                file,
                timestampedFileName,
                selectedProductId || undefined,
              );
            } catch (error) {
              message.error(t('BookPage.ErrorMessages.UploadFailed', { filename: file.name }));
              console.error('Upload error:', error);
            }
          }
        } catch (error) {
          message.error(t('BookPage.ErrorMessages.UploadError'));
          console.error('Drop handling error:', error);
        }
      }
    },
    [sessionId, setVisible, selectedProductId, isUploadEnabled, t],
  );

  // Maak een useRef voor de laatste dragState tijd om knipperen te voorkomen
  const lastDragStateChangeRef = useRef<number>(0);

  // Periodieke controle of drag nog steeds actief is - alleen voor fail-safe
  useEffect(() => {
    // Start een interval alleen als fail-safe, geen actieve detectie meer
    if (isDragging && dragCheckIntervalRef.current === null) {
      // Zet een backup timer om de overlay automatisch na 30 seconden te verbergen
      // Dit is een veiligheidsvoorziening voor het geval de drag-detectie helemaal mislukt
      const safetyTimeout = window.setTimeout(() => {
        setIsDragging(false);
      }, 30000); // 30 seconden

      // Maak de safetyTimeout beschikbaar voor cleanup
      return () => {
        window.clearTimeout(safetyTimeout);
      };
    }
  }, [isDragging]);

  // Toetsenbord event listener voor escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isDragging) {
        setIsDragging(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDragging]);

  // Event listeners registreren/verwijderen
  // Note: the listeners themselves (handleDragEnter/handleDragOver/handleDrop) already bail out
  // when !isUploadEnabled - they stay registered regardless so a drop anywhere on the page still
  // gets preventDefault() and a clear error message, instead of falling through to the browser's
  // default "open file" behaviour (which would navigate away and lose the session).
  useEffect(() => {
    // Zorg ervoor dat de event listeners alleen op document.body worden geplaatst
    const bodyElement = document.body;

    // Create native event handlers that work with DOM events
    const handleNativeDragEnter = (e: DragEvent) => {
      handleDragEnter(e as unknown as React.DragEvent<HTMLElement>);
    };

    const handleNativeDragLeave = (e: DragEvent) => {
      handleDragLeave(e as unknown as React.DragEvent<HTMLElement>);
    };

    const handleNativeDragOver = (e: DragEvent) => {
      handleDragOver(e as unknown as React.DragEvent<HTMLElement>);
    };

    const handleNativeDrop = (e: DragEvent) => {
      handleDrop(e as unknown as React.DragEvent<HTMLElement>);
    };

    // Event listeners registeren
    bodyElement.addEventListener('dragenter', handleNativeDragEnter);
    bodyElement.addEventListener('dragleave', handleNativeDragLeave);
    bodyElement.addEventListener('dragover', handleNativeDragOver);
    bodyElement.addEventListener('drop', handleNativeDrop);

    // Add event listener to window to catch when mouse leaves browser entirely
    const handleWindowLeave = () => {
      setIsDragging(false);
    };

    // Extra event listeners voor drag end en mouse up
    const handleDragEnd = () => {
      setIsDragging(false);
    };

    const handleMouseUp = () => {
      // Als de muis wordt losgelaten, controleer of we nog in drag mode zijn
      if (isDragging) {
        // Wacht even om te zien of drag events worden afgehandeld
        setTimeout(() => {
          // Als er geen drag events zijn, reset isDragging
          setIsDragging(false);
        }, 100);
      }
    };

    window.addEventListener('mouseleave', handleWindowLeave);
    window.addEventListener('dragend', handleDragEnd);
    window.addEventListener('mouseup', handleMouseUp);

    // Cleanup event listeners bij unmount
    return () => {
      bodyElement.removeEventListener('dragenter', handleNativeDragEnter);
      bodyElement.removeEventListener('dragleave', handleNativeDragLeave);
      bodyElement.removeEventListener('dragover', handleNativeDragOver);
      bodyElement.removeEventListener('drop', handleNativeDrop);
      window.removeEventListener('mouseleave', handleWindowLeave);
      window.removeEventListener('dragend', handleDragEnd);
      window.removeEventListener('mouseup', handleMouseUp);

      // Clear any timeout when component unmounts
      if (dragTimeoutRef.current !== null) {
        window.clearTimeout(dragTimeoutRef.current);
        dragTimeoutRef.current = null;
      }
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop, isDragging, session, isUploadEnabled]);

  if (isLoading || isLoadingProducts) {
    return (
      <SLoaderContainer>
        <Spin size="large" />
      </SLoaderContainer>
    );
  }

  if (isSessionError || isProductsError) {
    return (
      <Result
        status="500"
        title="500"
        subTitle={t('BookPage.GenericError')}
        extra={<Button type="primary">{t('BookPage.SendEmail')}</Button>}
      />
    );
  }

  // Handler for adding a new product
  const handleAddProduct = async () => {
    try {
      const newProduct = await API.addProduct(sessionId);
      // Store the pending product ID - handle both nested and direct response
      const productId = (newProduct as any).data?.id || newProduct.id;
      setPendingProductId(productId);
      // Invalidate and refetch the products query
      queryClient.invalidateQueries('products');
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.meta?.message || t('BookPage.ErrorMessages.AddingVariantFailed');
      message.error(errorMessage);
      console.error('Error adding product:', error);
      setPendingProductId(null); // Clear pending state on error
    }
  };

  // Handler for updating product count
  const handleUpdateCount = async (productId: string, newCount: number) => {
    try {
      await API.updateProductCount(sessionId, productId, newCount);
      // Invalidate and refetch the products query
      queryClient.invalidateQueries('products');
      // Clear editing state
      setEditingProductId(null);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.meta?.message || t('BookPage.ErrorMessages.UpdatingCountFailed');
      message.error(errorMessage);
      console.error('Error updating product count:', error);
    }
  };

  // Handler for deleting a product
  const handleDeleteProduct = async (productId: string) => {
    // Check if we can delete (must have at least 1 variant remaining)
    if (products.length <= 1) {
      message.error(t('BookPage.ErrorMessages.MinimumOneVariant'));
      return;
    }

    try {
      await API.deleteProduct(sessionId, productId);

      // If we deleted the currently selected product, select another one
      if (selectedProductId === productId) {
        const remainingProducts = products.filter((p) => p.id !== productId);
        if (remainingProducts.length > 0) {
          setSelectedProductId(remainingProducts[0].id);
        }
      }

      // Invalidate and refetch the products query
      queryClient.invalidateQueries('products');
      message.success(t('BookPage.SuccessMessages.VariantDeleted'));
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.meta?.message || t('BookPage.ErrorMessages.DeletingVariantFailed');
      message.error(errorMessage);
      console.error('Error deleting product:', error);
    }
  };

  // Determine UI visibility based on variant config
  const shouldShowTabs = variantFeaturesEnabled && maxVariants !== 1; // Show tabs when variant features enabled and not single variant mode
  const shouldShowAddButton =
    variantFeaturesEnabled &&
    !hasFixedQuantities &&
    maxVariants !== 1 &&
    (maxVariants === 0 || // Unlimited variants
      (needsExactCount && currentVariantCount < maxVariants) || // Exact count mode, need more variants
      (!needsExactCount && currentVariantCount < maxVariants)) && // Max count mode, can add more variants
    currentTotalQuantity < (session?.quantity || 0); // Also check total quantity limit

  // Helper function to determine tab status
  const getTabStatus = (product: any) => {
    const hasWarnings = product.sidesWithWarnings && product.sidesWithWarnings.length > 0;
    const expectedSidesCount = session?.sidesCount || 1;
    const actualSidesCount = product.sides ? product.sides.length : 0;
    const hasAllSides =
      actualSidesCount === expectedSidesCount && product.sides.every((side: any) => side.assetId);

    if (hasWarnings) return 'warning'; // Orange
    if (hasAllSides) return 'complete'; // Green
    return 'incomplete'; // Default/no indicator
  };

  // Create tabs items for each product with status indicators
  const productTabItems = products.map((product, index) => {
    const status = getTabStatus(product);

    return {
      key: product.id,
      label: (
        <div style={{ position: 'relative' }}>
          <span>{index + 1}</span>
          <div
            style={{
              position: 'absolute',
              bottom: '-6px',
              left: '-8px',
              right: '-8px',
              height: '2px',
              backgroundColor:
                status === 'warning' ? '#faad14' : status === 'complete' ? '#52c41a' : '#d9d9d9',
              borderRadius: '1px',
            }}
          />
        </div>
      ),
      children: null, // We'll render the content outside of the tabs
    };
  });

  // Add the + button as a special tab if we should show it
  const tabItems = shouldShowAddButton
    ? [
        ...productTabItems,
        {
          key: 'add-variant',
          label: <PlusOutlined style={{ fontSize: '12px' }} />,
          children: null,
          disabled: false,
        },
      ]
    : productTabItems;

  return (
    <SContainer>
      {/* Global Drag & Drop Overlay */}
      {isUploadEnabled && (
        <DragDropOverlay visible={isDragging} onClose={() => setIsDragging(false)} />
      )}

      <Box>
        {/* Product Tabs with Varianten label */}
        {products.length > 0 && shouldShowTabs && (
          <div
            style={{
              marginBottom: 16,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              position: 'relative',
            }}
          >
            <span style={{ fontSize: '14px', fontWeight: 500, color: '#666', lineHeight: '32px' }}>
              {t('BookPage.Variants')}
            </span>
            <Tabs
              activeKey={selectedProductId || undefined}
              onChange={(key) => {
                if (key === 'add-variant') {
                  handleAddProduct();
                } else {
                  setSelectedProductId(key);
                }
              }}
              items={tabItems}
              size="small"
              type="card"
              style={{ flex: 1 }}
            />
            <Tooltip
              title={
                quantityLocked
                  ? t('BookPage.FixedQuantitiesHint', {
                      current: currentTotalQuantity,
                      total: session?.quantity || 0,
                    })
                  : t('BookPage.QuickDistributeTooltip')
              }
            >
              <Button
                size="small"
                type={currentTotalQuantity === (session?.quantity || 0) ? 'default' : 'primary'}
                style={{
                  backgroundColor:
                    currentTotalQuantity === (session?.quantity || 0)
                      ? '#52c41a' // Groen als compleet
                      : '#faad14', // Oranje als onvolledig
                  borderColor:
                    currentTotalQuantity === (session?.quantity || 0) ? '#52c41a' : '#faad14',
                  color: 'white',
                  fontWeight: '500',
                  marginLeft: '8px',
                  minWidth: '50px',
                }}
                onClick={() => !quantityLocked && setShowQuantityPanel(!showQuantityPanel)}
              >
                {currentTotalQuantity}/{session?.quantity || 0}
                {!quantityLocked && ` ${t('BookPage.QuickDistribute')}`}
              </Button>
            </Tooltip>
          </div>
        )}

        {/* Bulk Quantity Management Panel */}
        {showQuantityPanel && products.length > 0 && variantFeaturesEnabled && !quantityLocked && (
          <div
            style={{
              position: 'absolute',
              top: '40px',
              right: '0px',
              width: '280px',
              padding: 12,
              backgroundColor: 'white',
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
            }}
          >
            <div
              style={{
                marginBottom: 12,
                fontSize: '14px',
                fontWeight: 500,
                color: '#333',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>{t('BookPage.AdjustQuantityPerVariant')}</span>
              <button
                onClick={() => setShowQuantityPanel(false)}
                style={{
                  width: '20px',
                  height: '20px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  color: '#999',
                  cursor: 'pointer',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                  e.currentTarget.style.color = '#666';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#999';
                }}
              >
                ×
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {products.map((product, index) => (
                <div
                  key={product.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    backgroundColor: 'white',
                    borderRadius: 4,
                    border:
                      selectedProductId === product.id ? '1px solid #1890ff' : '1px solid #d9d9d9',
                  }}
                >
                  <span
                    style={{
                      fontSize: '13px',
                      color: selectedProductId === product.id ? '#1890ff' : '#666',
                      fontWeight: selectedProductId === product.id ? 500 : 400,
                    }}
                  >
                    {t('BookPage.Variant', { number: index + 1 })}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <button
                      onClick={() => {
                        const newCount = Math.max(1, product.count - 1);
                        if (newCount !== product.count) {
                          handleUpdateCount(product.id, newCount);
                        }
                      }}
                      disabled={product.count <= 1}
                      style={{
                        width: '24px',
                        height: '28px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px',
                        backgroundColor: product.count <= 1 ? '#f5f5f5' : 'white',
                        color: product.count <= 1 ? '#ccc' : '#666',
                        cursor: product.count <= 1 ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min="1"
                      max={session?.quantity || 100}
                      value={product.count}
                      onChange={(e) => {
                        const newCount = parseInt(e.target.value) || 1;
                        const maxAllowed = session?.quantity || 100;

                        // Calculate what the new total would be
                        const otherProductsTotal = products
                          .filter((p) => p.id !== product.id)
                          .reduce((sum, p) => sum + p.count, 0);
                        const newTotal = otherProductsTotal + newCount;

                        // Only update if within limits
                        if (newTotal <= maxAllowed && newCount !== product.count && newCount >= 1) {
                          handleUpdateCount(product.id, newCount);
                        }
                      }}
                      style={{
                        width: '50px',
                        height: '28px',
                        fontSize: '13px',
                        textAlign: 'center',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px',
                        padding: '0 4px',
                      }}
                    />
                    <button
                      onClick={() => {
                        const newCount = product.count + 1;
                        const maxAllowed = session?.quantity || 100;

                        // Calculate what the new total would be
                        const otherProductsTotal = products
                          .filter((p) => p.id !== product.id)
                          .reduce((sum, p) => sum + p.count, 0);
                        const newTotal = otherProductsTotal + newCount;

                        // Only update if within limits
                        if (newTotal <= maxAllowed) {
                          handleUpdateCount(product.id, newCount);
                        }
                      }}
                      disabled={(() => {
                        const maxAllowed = session?.quantity || 100;
                        const otherProductsTotal = products
                          .filter((p) => p.id !== product.id)
                          .reduce((sum, p) => sum + p.count, 0);
                        return otherProductsTotal + product.count + 1 > maxAllowed;
                      })()}
                      style={{
                        width: '24px',
                        height: '28px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px',
                        backgroundColor: (() => {
                          const maxAllowed = session?.quantity || 100;
                          const otherProductsTotal = products
                            .filter((p) => p.id !== product.id)
                            .reduce((sum, p) => sum + p.count, 0);
                          return otherProductsTotal + product.count + 1 > maxAllowed
                            ? '#f5f5f5'
                            : 'white';
                        })(),
                        color: (() => {
                          const maxAllowed = session?.quantity || 100;
                          const otherProductsTotal = products
                            .filter((p) => p.id !== product.id)
                            .reduce((sum, p) => sum + p.count, 0);
                          return otherProductsTotal + product.count + 1 > maxAllowed
                            ? '#ccc'
                            : '#666';
                        })(),
                        cursor: (() => {
                          const maxAllowed = session?.quantity || 100;
                          const otherProductsTotal = products
                            .filter((p) => p.id !== product.id)
                            .reduce((sum, p) => sum + p.count, 0);
                          return otherProductsTotal + product.count + 1 > maxAllowed
                            ? 'not-allowed'
                            : 'pointer';
                        })(),
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      +
                    </button>
                  </div>
                  {products.length > 1 && !hasFixedQuantities && (
                    <Tooltip title={t('BookPage.RemoveVariant')}>
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteProduct(product.id)}
                        style={{
                          color: '#ff4d4f',
                          padding: '0 4px',
                          height: '28px',
                          minWidth: '28px',
                        }}
                      />
                    </Tooltip>
                  )}
                </div>
              ))}
            </div>
            <div
              style={{
                marginTop: 12,
                fontSize: '12px',
                color: '#666',
                textAlign: 'center',
              }}
            >
              {t('BookPage.Total', {
                current: currentTotalQuantity,
                total: session?.quantity || 0,
              })}
            </div>
          </div>
        )}

        {/* Add button for single product mode or when no tabs are shown but add button should be visible */}
        {products.length > 0 && !shouldShowTabs && shouldShowAddButton && (
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
            <Tooltip title={t('BookPage.AddVariant')}>
              <Button
                type="dashed"
                size="middle"
                icon={<PlusOutlined />}
                onClick={handleAddProduct}
                style={{
                  minWidth: 40,
                  height: 40,
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              />
            </Tooltip>
          </div>
        )}

        <Row gutter={[10, 10]}>
          <Col
            className="gutter-row"
            xs={{ span: 24 }}
            lg={{ span: 5 }}
            style={{ height: '95vh', overflow: 'auto' }}
          >
            {/* Variant Info Header */}
            {shouldShowTabs && variantFeaturesEnabled && selectedProduct && (
              <div
                style={{
                  marginBottom: 12,
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  fontSize: '14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>
                    <strong>
                      {t('BookPage.Variant', {
                        number: products.findIndex((p) => p.id === selectedProduct.id) + 1,
                      })}
                    </strong>
                  </span>
                  {products.length > 1 && !hasFixedQuantities && (
                    <Tooltip title={t('BookPage.RemoveVariant')}>
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteProduct(selectedProduct.id)}
                        style={{
                          color: '#ff4d4f',
                          padding: '0 4px',
                          height: '20px',
                          minWidth: '20px',
                        }}
                      />
                    </Tooltip>
                  )}
                </div>
                {quantityLocked && (
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    {t('BookPage.Quantity')}: <strong>{selectedProduct.count}</strong>
                  </span>
                )}
                {!quantityLocked && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '12px', color: '#666' }}>{t('BookPage.Quantity')}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <button
                      onClick={() => {
                        const newCount = Math.max(1, selectedProduct.count - 1);
                        if (newCount !== selectedProduct.count) {
                          handleUpdateCount(selectedProduct.id, newCount);
                        }
                      }}
                      disabled={selectedProduct.count <= 1}
                      style={{
                        width: '20px',
                        height: '24px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px',
                        backgroundColor: selectedProduct.count <= 1 ? '#f5f5f5' : 'white',
                        color: selectedProduct.count <= 1 ? '#ccc' : '#666',
                        cursor: selectedProduct.count <= 1 ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min="1"
                      max={session?.quantity || 100}
                      value={selectedProduct.count}
                      onChange={(e) => {
                        const newCount = parseInt(e.target.value) || 1;
                        const maxAllowed = session?.quantity || 100;

                        // Calculate what the new total would be
                        const otherProductsTotal = products
                          .filter((p) => p.id !== selectedProduct.id)
                          .reduce((sum, p) => sum + p.count, 0);
                        const newTotal = otherProductsTotal + newCount;

                        // Only update if within limits
                        if (
                          newTotal <= maxAllowed &&
                          newCount !== selectedProduct.count &&
                          newCount >= 1
                        ) {
                          handleUpdateCount(selectedProduct.id, newCount);
                        }
                      }}
                      style={{
                        width: '40px',
                        height: '24px',
                        fontSize: '12px',
                        textAlign: 'center',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px',
                        padding: '0 4px',
                      }}
                    />
                    <button
                      onClick={() => {
                        const newCount = selectedProduct.count + 1;
                        const maxAllowed = session?.quantity || 100;

                        // Calculate what the new total would be
                        const otherProductsTotal = products
                          .filter((p) => p.id !== selectedProduct.id)
                          .reduce((sum, p) => sum + p.count, 0);
                        const newTotal = otherProductsTotal + newCount;

                        // Only update if within limits
                        if (newTotal <= maxAllowed) {
                          handleUpdateCount(selectedProduct.id, newCount);
                        }
                      }}
                      disabled={(() => {
                        const maxAllowed = session?.quantity || 100;
                        const otherProductsTotal = products
                          .filter((p) => p.id !== selectedProduct.id)
                          .reduce((sum, p) => sum + p.count, 0);
                        return otherProductsTotal + selectedProduct.count + 1 > maxAllowed;
                      })()}
                      style={{
                        width: '20px',
                        height: '24px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px',
                        backgroundColor: (() => {
                          const maxAllowed = session?.quantity || 100;
                          const otherProductsTotal = products
                            .filter((p) => p.id !== selectedProduct.id)
                            .reduce((sum, p) => sum + p.count, 0);
                          return otherProductsTotal + selectedProduct.count + 1 > maxAllowed
                            ? '#f5f5f5'
                            : 'white';
                        })(),
                        color: (() => {
                          const maxAllowed = session?.quantity || 100;
                          const otherProductsTotal = products
                            .filter((p) => p.id !== selectedProduct.id)
                            .reduce((sum, p) => sum + p.count, 0);
                          return otherProductsTotal + selectedProduct.count + 1 > maxAllowed
                            ? '#ccc'
                            : '#666';
                        })(),
                        cursor: (() => {
                          const maxAllowed = session?.quantity || 100;
                          const otherProductsTotal = products
                            .filter((p) => p.id !== selectedProduct.id)
                            .reduce((sum, p) => sum + p.count, 0);
                          return otherProductsTotal + selectedProduct.count + 1 > maxAllowed
                            ? 'not-allowed'
                            : 'pointer';
                        })(),
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      +
                    </button>
                  </div>
                </span>
                )}
              </div>
            )}

            <AssetsSidebar
              showInstructions={false}
              showAddBlankPage={true}
              shouldFetchAssets={true}
              autoScrollDependency={false}
              scrollContainerRef={sideslistScrollRef}
            >
              <ProductSidesPreviewsList
                sides={sides}
                pdfPreviews={pdfPreviews}
                scrollContainerRef={sideslistScrollRef}
              />
            </AssetsSidebar>
          </Col>
          <Col className="gutter-row" xs={{ span: 24 }} lg={{ span: isRightBarEnabled ? 14 : 19 }}>
            <Card
              style={{ overflow: 'auto', maxHeight: '100vh' }}
              onDragEnter={(e) => {
                // Voorkom dat de Card de drag events stopt en houd isDragging aan
                e.preventDefault();
                e.stopPropagation();
                // Handhaaf isDragging actief tijdens het slepen over de Card
                if (e.dataTransfer?.types.includes('Files')) {
                  updateDraggingState(true);
                }
              }}
              onDragOver={(e) => {
                // Voorkom dat de Card de drag events stopt
                e.preventDefault();
                e.stopPropagation();
                // Zorg dat isDragging actief blijft
                if (e.dataTransfer?.types.includes('Files')) {
                  e.dataTransfer.dropEffect = 'copy';
                  updateDraggingState(true);
                }
              }}
              onDragLeave={(e) => {
                // NOOIT isDragging uitzetten binnen de Card
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                // Zorg ervoor dat de drop events worden doorgegeven
                e.preventDefault();
                e.stopPropagation();
                handleDrop(e);
              }}
            >
              {shouldShowBook ? (
                <PageCardEditor
                  side={sides[selectedSideIndex]}
                  onPdfPreviewGenerated={(previewUrl) => {
                    handlePdfPreviewGenerated(sides[selectedSideIndex].id, previewUrl);
                  }}
                  // Geef ook drag event handlers door aan PageCardEditor
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={(e) => {
                    // Alleen voor document/body level events
                    e.stopPropagation();
                  }}
                  onDrop={handleDrop}
                />
              ) : (
                <Empty description={t('BookPage.NoFilesUploaded')} />
              )}
            </Card>
          </Col>
          {shouldShowBook && isRightBarEnabled && (
            <Col className="gutter-row" xs={{ span: 24 }} lg={{ span: 5 }}>
              <ResultOutput
                side={sides[selectedSideIndex]}
                totalPageCount={sides.length}
                sideNumber={selectedSideIndex + 1}
              />
            </Col>
          )}
        </Row>
      </Box>
    </SContainer>
  );
};

export default Book;
